import { useEffect, useMemo, useState } from 'react';
import { PhoneField, SelectField, TextArea, TextField } from '../components/FormField';
import Section from '../components/Section';
import { scrollToFirstError } from '../utils/scrollToFirstError';
import {
  DEFAULT_SERVICE,
  SERVICE_OPTIONS,
  applyEndedStatus,
  formatCoverage,
  serviceKey,
  syncPlantHistories,
} from '../services/suppliers';
import {
  COUNTRY_OPTIONS,
  STATE_OPTIONS_BY_COUNTRY,
  countryHasStateList,
  countryRequiresState,
} from '../utils/addressOptions';

// Per-contact plant assignment was removed — contacts are now plain
// "who to call" entries that live at the supplier level, not scoped
// to a specific plant. Old records may still have a `plants` field
// in their JSON; the submit handler strips it on save so the data
// gradually cleans itself up as suppliers are edited.
function emptyContact() {
  return {
    name: '',
    title: '',
    // Free-form location label (city, plant, region — whatever the
    // Director cares to record). Surfaced as its own column on the
    // Supplier detail page right after Title.
    location: '',
    email: '',
    phone: '',
    officePhone: '',
    role: '',
  };
}

// Legacy helper retained for any orphan callers; the form no longer
// uses it now that contacts don't carry a plants field. Kept inert
// here rather than deleted so a future re-introduction has a clean
// reference point.
// eslint-disable-next-line no-unused-vars
function normaliseContactPlants(value) {
  if (Array.isArray(value)) {
    return value.map((v) => String(v || '').trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [];
}

// Per-plant billing assignment. Each assignment records the supplier's hourly
// rate and hours budgeted at a specific plant. hoursUnitOther is only used
// when hoursUnit === 'Other'. coverageStatus is the per-plant lifecycle flag
// (Active / Paused / Closed) and defaults to Active for fresh rows.
function emptyPlantAssignment() {
  return {
    plantCode: '',
    // What this row bills for at the plant. A supplier can list the SAME
    // plant more than once, one row per service, each with its own rate
    // and hours (per Jillian, Sep 2026 — e.g. Nascote at BGA: Standard
    // Coverage at $35 and Lineside Support at $30). (plant, service) is
    // the unique key. Defaults to the standard name so single-service
    // suppliers never have to touch it.
    service: DEFAULT_SERVICE,
    hourlyRate: '',
    // Rate history lives on the assignment so Billing can price each
    // day of a timesheet at the rate that was in effect on that day.
    // Empty on a brand-new row; the service's normaliseRateHistory
    // will seed a single entry from `hourlyRate` at first save.
    hourlyRateHistory: [],
    hoursAllocated: '',
    hoursUnit: '',
    hoursUnitOther: '',
    // Dated coverage changes (Oct 2026), edited in the Coverage History
    // box. The top-level hours / unit above mirror today's entry.
    coverageHistory: [],
    coverageStatus: 'Active',
    // ISO date (YYYY-MM-DD) the supplier started covering this plant.
    // Optional — empty means unknown / not recorded. Surfaced beside
    // Coverage Status on both the form and the Supplier detail page.
    startDate: '',
    // ISO date the supplier's coverage at this plant ends. Optional —
    // blank means open-ended. After it passes, the tracker / billing /
    // invoice alert treat the plant as no longer covered (Sep 2026).
    endDate: '',
    // Weekend billing (Nov 2026): whether this supplier pays for Saturday /
    // Sunday work at this plant. Unticked days auto-fill as 0 hours on the
    // Billing Tracker. Only applies to Per Day / Per Shift coverage (weekly
    // and monthly suppliers bill in one lump). New rows start unticked
    // (per Jillian, Nov 2026) so weekend billing is a deliberate choice.
    // Older rows with no value also count as unticked; only an explicit
    // tick means billed.
    billSaturdays: false,
    billSundays: false,
    // Free-form per-plant notes ("works night shift only", "billed
    // through parent company", etc.). Optional, multi-line.
    notes: '',
  };
}

// One editable PO row (per plant). `file` holds the attached PO document
// (a File object) until Save — it never travels with the saved model.
function emptyPo() {
  return {
    plantCode: '',
    // 'Yearly PO' | 'Quarterly PO' | 'Monthly PO' | 'PO Pending'
    type: '',
    number: '',
    // Held as a string while typing; cast to number on save.
    amount: '',
    // Blanket-only ISO dates (YYYY-MM-DD).
    initiationDate: '',
    expirationDate: '',
    // Free-text notes for this PO (all types) — per Jillian, Aug 2026.
    notes: '',
    file: null,
  };
}

// Sentinel plant-code meaning "this one PO covers every plant" — the
// dropdown's Select All option. Form-only: on save the PO is copied onto
// each plant assignment, so SharePoint never stores this value and every
// downstream reader (PO Tracker, Invoice Draft, Billing) still sees a
// plain per-plant PO.
const ALL_PLANTS = '__ALL__';

// Build the editable PO rows from the supplier's plant assignments. The
// service's decode carries a legacy supplier-level PO onto the first
// plant assignment, so pre-per-plant suppliers still show one row here.
function buildPosFromAssignments(initial) {
  const assignments = (initial || {}).plantAssignments || [];
  // POs are per PLANT, not per service row — several rows at the same
  // plant share one PO. Keep the first row per plant so a two-service
  // plant doesn't render two identical PO cards.
  const seenPoPlants = new Set();
  const rows = assignments
    .filter((pa) => pa?.po?.type)
    .filter((pa) => {
      const code = String(pa.plantCode || '').toUpperCase().trim();
      if (seenPoPlants.has(code)) return false;
      seenPoPlants.add(code);
      return true;
    })
    .map((pa) => ({
      plantCode: String(pa.plantCode || '').toUpperCase().trim(),
      type: pa.po.type,
      number: pa.po.number || '',
      amount: pa.po.amount == null ? '' : String(pa.po.amount),
      initiationDate: pa.po.initiationDate || '',
      expirationDate: pa.po.expirationDate || '',
      notes: pa.po.notes || '',
      file: null,
    }));
  // Collapse to a single "All Plants" row when every assigned plant
  // carries the exact same PO — that's the shape the Select All option
  // saves, so re-opening the form shows one shared row instead of one
  // identical copy per plant.
  const poKey = (r) =>
    [
      r.type,
      (r.number || '').trim().toUpperCase(),
      String(r.amount).trim(),
      r.initiationDate || '',
      r.expirationDate || '',
      (r.notes || '').trim(),
    ].join('|');
  const distinctPlantCount = new Set(
    assignments.map((pa) => String(pa.plantCode || '').toUpperCase().trim()),
  ).size;
  if (
    rows.length >= 2 &&
    rows.length === distinctPlantCount &&
    rows.every((r) => poKey(r) === poKey(rows[0]))
  ) {
    return [{ ...rows[0], plantCode: ALL_PLANTS }];
  }
  return rows;
}

// Coverage days options. Listed in the natural lifecycle order
// (shortest workweek first) so the dropdown reads predictably.

// Dropdown presets. "Other" reveals a free-text fill-in so Jillian can
// capture values that don't fit the canned list.
const HOURS_UNITS = ['Per Day', 'Per Week', 'Per Shift', 'Per Month'];

// Weekly / monthly coverage bills in one lump (Monday / the 15th), so the
// weekend billing checkboxes don't apply to it.
function isLumpSumUnit(unit) {
  const u = String(unit || '').trim().toLowerCase();
  return u === 'per week' || u === 'per month';
}
// Per-plant coverage lifecycle. Listed in lifecycle order so the
// dropdown reads top-to-bottom the way a supplier moves through it:
// blue Prospecting → purple Proposal Out → green Active → teal On
// Call (active, standing by) → amber Paused → orange Suspended (a
// step more serious than Paused — typically compliance / dispute
// related) → red Closed → gray Declined (terminal "no" — distinct
// from Closed, which represents a relationship that ran and ended).
// Color palette mirrors the Part Status vocabulary so the visual
// scan stays consistent across the portal (see CoverageStatusBadge
// in SupplierDetail.jsx).
const COVERAGE_STATUSES = [
  'Prospecting',
  'Proposal Out',
  'Active',
  'On Call',
  'Paused',
  'Suspended',
  'Closed',
  'Declined',
];
const REPORTING_CADENCE_PRESETS = ['Daily', 'Weekly'];
// 4-4-5 is a common retail/auto accounting calendar — four weeks, four
// weeks, five weeks per quarter — so invoices line up with the OEM's fiscal
// schedule. "First of Month to Last of Month" is the plain calendar-month
// run.
// Standard net-payment terms. "Other" reveals a free-text fill-in so
// Jillian can capture supplier-specific arrangements (e.g. "2/10 Net
// 30", "Due on Receipt") that don't fit the canned list.
const PAYMENT_TERMS_PRESETS = ['Net 30', 'Net 60', 'Net 90'];
const BILLING_CYCLE_PRESETS = [
  'Weekly',
  'Monthly',
  '4-4-5',
  'First of Month to Last of Month',
];

// Shared style for the bordered repeater cards (Plant Locations and
// Contacts). Extracted so both sections stay visually identical.
const CARD_STYLE = {
  border: '1px solid var(--brand-border)',
  borderRadius: 6,
  padding: 12,
  position: 'relative',
};
const CARD_HEADER_STYLE = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 8,
};
const CARD_LABEL_STYLE = {
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: 'var(--brand-muted)',
};
const ADD_BUTTON_ROW_STYLE = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  marginBottom: 8,
};

function buildModel(initial, proposedCode) {
  return {
    Title: '',
    // Dun & Bradstreet D-U-N-S number — optional 9-digit identifier used
    // on supplier paperwork. We strip non-digits on input and only
    // validate the length if the user has actually typed something, so
    // leaving it blank is fine.
    DunsNumber: '',
    // Tax ID # — optional free-text identifier (EIN / VAT / etc.).
    TaxId: '',
    // New suppliers get the proposed next-available code pre-filled so
    // Jillian doesn't have to go hunt through the billing list. Editing
    // an existing supplier keeps whatever code is on the record.
    SupplierCode: proposedCode || '',
    ReportingCadence: '',
    BillingCycle: '',
    // Purchase-order requirement — Yes/No. Stored as text so Excel /
    // Power BI exports read cleanly without a boolean-to-label dance.
    // The POs themselves are per-plant — see `pos` below.
    PoRequired: '',
    // Overage-approval policy. When 'Yes', a liaison must attach written
    // approval from the supplier on the Billing Tracker before submitting
    // any week where this supplier's hours go over standard (a day/week
    // over standard, or an OT Hours row). Stored as 'Yes' or '' so it
    // exports the same way PoRequired does.
    RequireOverageApproval: '',
    // Net-payment terms (Net 30 / Net 60 / Net 90 / Other). Optional;
    // used by the Invoice Draft page to label and date-stamp the
    // outgoing invoice. When set to a preset, the value is stored
    // verbatim; "Other" stores whatever free-text the user typed.
    PaymentTerms: '',
    // Street address — stored in the SharePoint `AddressStreet` column.
    // (An earlier version of this form wrote to `Address`, which didn't
    // match any column and was silently dropped on save.)
    AddressStreet: '',
    // Optional additional address lines (suite, building, etc.).
    AddressStreet2: '',
    AddressStreet3: '',
    City: '',
    State: '',
    PostalCode: '',
    Country: '',
    // Stored as an ISO date string (YYYY-MM-DD) when the "New supplier
    // paperwork completed" checkbox is ticked. Empty string means not
    // yet complete. Kept separate from a boolean so the Supplier page
    // can show the actual completion date.
    NewSupplierPaperworkDate: '',
    Notes: '',
    contacts: [emptyContact()],
    plantAssignments: [],
    ...(initial || {}),
    // Per-plant PO rows (one per plant, Blanket or Monthly). Placed after
    // the spread so it's always derived fresh from the decoded record.
    pos: buildPosFromAssignments(initial),
  };
}

// Suggest the next available supplier code: the smallest unused
// integer at or above 100 (the historic floor — current codes live in
// the 100–574 range). This deliberately fills in holes from deleted
// or skipped suppliers rather than always pushing the sequence
// upward. Example: if 137–140 and 144 exist, the next suggestion is
// 141, then 142, then 143, then 145, then 146… When the list is
// empty, 100 is returned. Zero-padded to 3 digits to match how codes
// are typed elsewhere.
function proposeNextCode(existingCodes) {
  const taken = new Set();
  for (const c of existingCodes) {
    // Only purely numeric codes take part in the auto-numbering. Account
    // numbers may also be alphanumeric (up to 4 characters, per Jillian,
    // Sep 2026) — those are typed by hand and shouldn't shift the
    // suggested next number (parseInt would read "12AB" as 12).
    const str = String(c || '').trim();
    if (!/^\d+$/.test(str)) continue;
    const n = parseInt(str, 10);
    if (Number.isFinite(n) && n > 0) taken.add(n);
  }
  let candidate = 100;
  while (taken.has(candidate)) candidate += 1;
  return String(candidate).padStart(3, '0');
}

// Given the stored ReportingCadence/BillingCycle string, work out whether
// it matches one of our presets or should open the "Other" fill-in box.
// Returns { mode, customValue } where mode is one of the preset strings
// or 'Other'.
function splitCadence(value, presets) {
  const raw = (value || '').trim();
  if (!raw) return { mode: '', customValue: '' };
  if (presets.includes(raw)) return { mode: raw, customValue: '' };
  return { mode: 'Other', customValue: raw };
}

/**
 * Supplier create / edit form. Sections match the other portal forms
 * (PartForm, PlantForm, TeamForm): Supplier Info → Supplier Address →
 * Plant Locations & Coverage → Supplier Contacts. Contacts and Plant
 * Locations are inline repeater cards.
 */
export default function SupplierForm({
  initial,
  existingCodes,
  // Full plants list — needed so the "Plant Locations" repeater can
  // show a dropdown of every plant code + name. If the caller doesn't
  // pass it, the dropdown just shows whatever PlantCode strings are
  // already saved on the supplier.
  plants = [],
  readOnly = false,
  saving = false,
  // Directors can edit the supplier account number on an existing
  // record (everyone else sees it locked, as before). The override
  // is intentionally low-friction — Directors are the only role
  // expected to do account-number cleanup — but it surfaces a
  // warning since changing the code doesn't auto-update timesheets,
  // parts, or invoices that already reference the old code.
  isDirector = false,
  // Whether the editor may see billing information — the Billing &
  // Invoicing section (payment schedule, POs, payment terms) and the
  // receipt-policy checkbox. Directors only (per Jillian, July 2026);
  // a non-Director editor (e.g. a per-user supplier-edit override)
  // edits everything else while the saved billing values pass through
  // untouched. Defaults to true so legacy call sites keep working.
  canSeeBilling = true,
  onSave,
  onCancel,
}) {
  const isNew = !initial?.id;
  // Snapshot the original code at mount so the rename-warning + the
  // self-aware uniqueness check below can compare against the value
  // the supplier had before the user started editing. Stays stable
  // across edits (intentionally not re-derived inside an effect).
  const originalCode = useMemo(
    () => String((initial || {}).SupplierCode || '').trim(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Memoise the suggestion so the same value is used for the initial
  // state and the reset effect. Treat the prop defensively — it can be
  // a Set or an array from the caller, or missing entirely.
  const proposedCode = useMemo(() => {
    if (!isNew) return undefined;
    const codes = existingCodes
      ? existingCodes instanceof Set
        ? existingCodes
        : new Set(existingCodes)
      : new Set();
    return proposeNextCode(codes);
  }, [existingCodes, isNew]);

  // Build a fast-lookup set for duplicate detection. Normalised to
  // uppercase strings so the comparison matches how codes are stored.
  const takenCodes = useMemo(() => {
    const set = new Set();
    if (existingCodes) {
      for (const c of existingCodes) {
        if (c != null && c !== '') set.add(String(c).toUpperCase().trim());
      }
    }
    return set;
  }, [existingCodes]);

  const [model, setModel] = useState(() => buildModel(initial, proposedCode));
  const [errors, setErrors] = useState({});

  // Preset-vs-"Other" mode for the two cadence fields. Stored in local
  // state (not the model) so the user can flip between modes without
  // losing what they typed. On save we read from model.ReportingCadence /
  // model.BillingCycle — the source of truth — and these flags just
  // control which input is rendered.
  const initialCadence = splitCadence(
    (initial || {}).ReportingCadence,
    REPORTING_CADENCE_PRESETS,
  );
  const initialBilling = splitCadence(
    (initial || {}).BillingCycle,
    BILLING_CYCLE_PRESETS,
  );
  const initialPaymentTerms = splitCadence(
    (initial || {}).PaymentTerms,
    PAYMENT_TERMS_PRESETS,
  );
  const [cadenceMode, setCadenceMode] = useState(initialCadence.mode);
  const [billingMode, setBillingMode] = useState(initialBilling.mode);
  const [paymentTermsMode, setPaymentTermsMode] = useState(
    initialPaymentTerms.mode,
  );

  useEffect(() => {
    setModel(buildModel(initial, proposedCode));
    setErrors({});
    setCadenceMode(
      splitCadence((initial || {}).ReportingCadence, REPORTING_CADENCE_PRESETS).mode,
    );
    setBillingMode(
      splitCadence((initial || {}).BillingCycle, BILLING_CYCLE_PRESETS).mode,
    );
    setPaymentTermsMode(
      splitCadence((initial || {}).PaymentTerms, PAYMENT_TERMS_PRESETS).mode,
    );
  }, [initial?.id, proposedCode]);

  const set = (k, v) => setModel((m) => ({ ...m, [k]: v }));

  // Country / State dropdown options. We include the currently-saved value as
  // an extra option when it isn't in the canned list, so editing a legacy
  // supplier whose country/state was free-typed (e.g. "USA", "MI") still shows
  // the saved value instead of blanking it.
  const countryOptions = useMemo(() => {
    const opts = [...COUNTRY_OPTIONS];
    if (model.Country && !opts.includes(model.Country)) opts.unshift(model.Country);
    return opts;
  }, [model.Country]);
  const stateOptions = useMemo(() => {
    const base = STATE_OPTIONS_BY_COUNTRY[model.Country] || [];
    const opts = [...base];
    if (model.State && !opts.includes(model.State)) opts.unshift(model.State);
    return opts;
  }, [model.Country, model.State]);

  // Flipping to a preset writes the preset through; flipping to "Other"
  // clears the stored value so the fill-in starts empty.
  const onCadenceModeChange = (mode) => {
    setCadenceMode(mode);
    set('ReportingCadence', mode === 'Other' ? '' : mode);
  };
  const onBillingModeChange = (mode) => {
    setBillingMode(mode);
    set('BillingCycle', mode === 'Other' ? '' : mode);
  };
  const onPaymentTermsModeChange = (mode) => {
    setPaymentTermsMode(mode);
    set('PaymentTerms', mode === 'Other' ? '' : mode);
  };

  // Contact repeater handlers ---------------------------------------------
  const setContact = (i, patch) =>
    setModel((m) => {
      const next = [...(m.contacts || [])];
      next[i] = { ...next[i], ...patch };
      return { ...m, contacts: next };
    });
  const addContact = () =>
    setModel((m) => ({ ...m, contacts: [...(m.contacts || []), emptyContact()] }));
  const removeContact = (i) =>
    setModel((m) => ({
      ...m,
      contacts: (m.contacts || []).filter((_, idx) => idx !== i),
    }));
  // Reorder contacts within the Supplier Contacts list. `dir` is -1
  // (move up) or +1 (move down). Out-of-range moves are silent no-ops
  // so the up/down buttons can call this without bounds-checking.
  // Order is persisted on save like the rest of the contacts array.
  const moveContact = (i, dir) =>
    setModel((m) => {
      const list = [...(m.contacts || [])];
      const j = i + dir;
      if (j < 0 || j >= list.length) return m;
      [list[i], list[j]] = [list[j], list[i]];
      return { ...m, contacts: list };
    });

  // Plant-assignment repeater handlers ------------------------------------
  const setPlantAssignment = (i, patch) =>
    setModel((m) => {
      const next = [...(m.plantAssignments || [])];
      next[i] = { ...next[i], ...patch };
      return { ...m, plantAssignments: next };
    });
  // New plant rows go to the TOP of the list (per Jillian, Sep 2026) so the
  // blank card is right under the "+ Add Plant" button instead of below
  // every existing plant. The saved order follows the on-screen order.
  const addPlantAssignment = () =>
    setModel((m) => ({
      ...m,
      plantAssignments: [emptyPlantAssignment(), ...(m.plantAssignments || [])],
    }));
  const removePlantAssignment = (i) =>
    setModel((m) => ({
      ...m,
      plantAssignments: (m.plantAssignments || []).filter((_, idx) => idx !== i),
    }));

  // Per-plant PO repeater handlers ----------------------------------------
  const setPo = (i, patch) =>
    setModel((m) => {
      const next = [...(m.pos || [])];
      next[i] = { ...next[i], ...patch };
      return { ...m, pos: next };
    });
  const addPo = () =>
    setModel((m) => ({ ...m, pos: [...(m.pos || []), emptyPo()] }));
  const removePo = (i) =>
    setModel((m) => ({
      ...m,
      pos: (m.pos || []).filter((_, idx) => idx !== i),
    }));

  // Plants this supplier is assigned to — the only valid targets for a
  // per-plant PO. Uppercased so PO rows and assignments compare cleanly.
  const assignedPlantCodes = useMemo(
    () =>
      Array.from(
        new Set(
          (model.plantAssignments || [])
            .map((pa) => String(pa.plantCode || '').toUpperCase().trim())
            .filter(Boolean),
        ),
      ),
    [model.plantAssignments],
  );

  // "SHAP — Sterling Heights"-style labels for the PO plant dropdown,
  // mirroring the Plant Locations repeater.
  const plantLabelByCode = useMemo(
    () =>
      new Map(
        (plants || [])
          .filter((p) => p.PlantCode)
          .map((p) => [
            String(p.PlantCode).toUpperCase(),
            p.Title ? `${p.PlantCode} — ${p.Title}` : p.PlantCode,
          ]),
      ),
    [plants],
  );

  const validate = () => {
    const e = {};

    // --- Supplier Info ---
    // Empty-required messages are kept short ("Required") so they
    // match the consistent treatment used elsewhere — the form's
    // asterisks already tell you *what* is required; the message
    // just confirms it's missing. Format-validation messages
    // (DUNS digits, duplicate code, etc.) keep their fuller wording
    // because they convey real diagnostic info.
    if (!model.Title?.trim()) e.Title = 'Required';
    const rawCode = (model.SupplierCode || '').trim();
    if (!rawCode) {
      e.SupplierCode = 'Required';
    } else if (!/^[A-Za-z0-9]{1,4}$/.test(rawCode)) {
      e.SupplierCode = 'Letters and numbers only, up to 4 characters.';
    } else {
      // Uniqueness check. Two paths:
      //   • New supplier — straight membership test against takenCodes.
      //   • Director-edited existing supplier — same test but excluding
      //     the supplier's own original code so saving without changing
      //     the field doesn't trip the duplicate detector.
      // Non-Director edits never reach this branch because the input
      // stays disabled, but we re-check defensively in case the
      // disabled state was bypassed somehow.
      const upper = rawCode.toUpperCase();
      const originalUpper = originalCode.toUpperCase();
      const collides = takenCodes.has(upper) && upper !== originalUpper;
      if (isNew && takenCodes.has(upper)) {
        e.SupplierCode = `Account number ${rawCode} is already in use. Try ${proposedCode}.`;
      } else if (!isNew && isDirector && collides) {
        e.SupplierCode = `Account number ${rawCode} is already used by another supplier.`;
      }
    }
    // DUNS Number is optional, but if the user typed anything it must
    // be the full 9 digits — a partial D-U-N-S isn't useful on paperwork.
    const rawDuns = (model.DunsNumber || '').trim();
    if (rawDuns && !/^\d{9}$/.test(rawDuns)) {
      e.DunsNumber = 'DUNS Number must be 9 digits.';
    }
    if (!cadenceMode) {
      e.ReportingCadence = 'Required';
    } else if (cadenceMode === 'Other' && !(model.ReportingCadence || '').trim()) {
      e.ReportingCadence = 'Please specify the Reporting Schedule.';
    }
    // Billing fields are hidden from non-Director editors — skip their
    // required checks so a hidden field can never block the save. The
    // stored values pass through the model untouched.
    if (canSeeBilling) {
    if (!billingMode) {
      e.BillingCycle = 'Required';
    } else if (billingMode === 'Other' && !(model.BillingCycle || '').trim()) {
      e.BillingCycle = 'Please specify the Bill Payment Schedule.';
    }
    if (!model.PoRequired) e.PoRequired = 'Required';
    // Once PO Required is "Yes", at least one per-plant PO is needed.
    // Each PO row needs a plant (unique — one PO per plant) and a type.
    // Number, amount, and the Yearly/Quarterly start/expiration dates are
    // all optional (per Jillian, Aug 2026).
    if (model.PoRequired === 'Yes') {
      const poRows = model.pos || [];
      const seenPlants = new Set();
      const assigned = new Set(assignedPlantCodes);
      const posRowErrors = poRows.map((po) => {
        const pe = {};
        const code = String(po.plantCode || '').toUpperCase().trim();
        if (!code) {
          pe.plantCode = 'Required';
        } else if (code === ALL_PLANTS) {
          // "All Plants" covers everything, so it must be the only row.
          if (poRows.length > 1) {
            pe.plantCode =
              'Remove the other POs — "All Plants" already covers every plant.';
          }
        } else if (seenPlants.has(code)) {
          pe.plantCode = 'This plant already has a PO.';
        } else {
          seenPlants.add(code);
          if (!assigned.has(code)) {
            pe.plantCode = 'Not one of this supplier’s plant locations.';
          }
        }
        if (!po.type) pe.type = 'Required';
        // PO Number and PO Amount are optional (per Jillian, Aug 2026).
        // Amount is still validated as a positive number IF one is entered.
        if (po.amount !== '' && po.amount != null) {
          const amt = Number(po.amount);
          if (!Number.isFinite(amt) || amt <= 0) {
            pe.amount = 'Enter an amount greater than 0.';
          }
        }
        // Start/Expiration dates are optional for Yearly / Quarterly POs
        // (per Jillian, Aug 2026). Only validate the window when BOTH are
        // provided — an inverted window would silently exclude every
        // invoice from the balance drawdown.
        if (
          (po.type === 'Yearly PO' || po.type === 'Quarterly PO') &&
          (po.initiationDate || '').trim() &&
          (po.expirationDate || '').trim() &&
          po.expirationDate < po.initiationDate
        ) {
          pe.expirationDate =
            'Expiration Date must be on or after the Start Date.';
        }
        return pe;
      });
      if (poRows.length === 0) {
        e.pos = 'Add at least one PO, or set PO Required to "No".';
      } else if (posRowErrors.some((pe) => Object.keys(pe).length > 0)) {
        e.posRows = posRowErrors;
      }
    }
    // Payment terms is optional, but if "Other" is picked the
    // fill-in must have content — otherwise the saved record would
    // have an empty PaymentTerms despite the picker showing Other.
    if (
      paymentTermsMode === 'Other' &&
      !(model.PaymentTerms || '').trim()
    ) {
      e.PaymentTerms = 'Please specify the payment terms.';
    }
    } // end canSeeBilling

    // --- Supplier Address ---
    if (!model.AddressStreet?.trim()) e.AddressStreet = 'Required';
    if (!model.City?.trim()) e.City = 'Required';
    if (countryRequiresState(model.Country) && !model.State?.trim()) {
      e.State = 'Required';
    }
    if (!model.PostalCode?.trim()) e.PostalCode = 'Required';
    if (!model.Country?.trim()) e.Country = 'Required';

    // --- Plant Locations ---
    // Require at least one plant location, and every field within every
    // row must be filled. Per-row errors are indexed so the UI can render
    // them inline on the correct card.
    const plantRows = model.plantAssignments || [];
    // The same plant may appear more than once, one row per SERVICE
    // (Sep 2026). What must be unique is the (plant, service) pair.
    const seenPlantServices = new Set();
    const plantRowErrors = plantRows.map((pa) => {
      const pe = {};
      const paCode = String(pa.plantCode || '').toUpperCase().trim();
      // Blank service saves as the default name, so it's never "Required".
      const paService = String(pa.service || '').trim();
      if (!paCode) {
        pe.plantCode = 'Required';
      } else {
        const key = `${paCode}|${serviceKey(paService || DEFAULT_SERVICE)}`;
        if (seenPlantServices.has(key)) {
          pe.service = `This plant already has a "${paService || DEFAULT_SERVICE}" row. Give this one a different service name.`;
        } else {
          seenPlantServices.add(key);
        }
      }
      // Rate History is Director-only (canSeeBilling); other editors
      // save the stored rates untouched, so they aren't asked for one.
      if (canSeeBilling && !(pa.hourlyRateHistory || []).length) {
        pe.hourlyRateHistory = 'Add the starting rate.';
      }
      if (pa.endDate && pa.startDate && String(pa.endDate) < String(pa.startDate)) {
        pe.endDate = 'End Date must be on or after Start Date.';
      }
      if (!(pa.coverageHistory || []).length) {
        pe.coverageHistory = 'Add the starting coverage.';
      }
      return pe;
    });
    if (plantRows.length === 0) {
      e.plantAssignments = 'At least one plant location is required.';
    } else if (plantRowErrors.some((pe) => Object.keys(pe).length > 0)) {
      e.plantAssignmentsRows = plantRowErrors;
    }

    // --- Supplier Contacts ---
    // Require at least one contact. On each contact, Name + Title + Email
    // are required; Phone, Plant/s, and Part/s are optional.
    const contactRows = model.contacts || [];
    const contactRowErrors = contactRows.map((c) => {
      const ce = {};
      if (!c.name?.trim()) ce.name = 'Required';
      if (!c.title?.trim()) ce.title = 'Required';
      if (!c.email?.trim()) ce.email = 'Required';
      return ce;
    });
    if (contactRows.length === 0) {
      e.contacts = 'At least one contact is required.';
    } else if (contactRowErrors.some((ce) => Object.keys(ce).length > 0)) {
      e.contactsRows = contactRowErrors;
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = (e) => {
    e.preventDefault();
    if (!validate()) {
      scrollToFirstError();
      return;
    }
    // Validation guarantees every contact has name/title/email, so no
    // filtering needed. Strip the legacy `plants` field on the way
    // out so suppliers saved after the per-contact plant assignment
    // was retired drop the unused data from their JSON.
    // eslint-disable-next-line no-unused-vars
    const contacts = (model.contacts || []).map(({ plants: _plants, ...rest }) => rest);
    // Per-plant POs, keyed by plant code, so each plant assignment can
    // carry its PO (and derive its legacy currentPO field) below. When
    // PO Required is "No" the map is empty and every assignment's PO is
    // cleared on save.
    const activePos = model.PoRequired === 'Yes' ? model.pos || [] : [];
    // A single "All Plants" row (the dropdown's Select All) means this
    // one PO is copied onto every plant assignment below.
    const allPlantsPo =
      activePos.find(
        (po) => String(po.plantCode || '').toUpperCase().trim() === ALL_PLANTS,
      ) || null;
    const posByPlant = new Map(
      activePos.map((po) => [
        String(po.plantCode || '').toUpperCase().trim(),
        po,
      ]),
    );

    // Plant assignments: cast the two numeric fields to numbers, and
    // collapse the hoursUnit "Other" fill-in down to the literal unit
    // string so downstream reports see one clean value.
    const plantAssignments = (model.plantAssignments || [])
      .map((pa) => {
        // Rates and coverage are edited as dated histories (Oct 2026);
        // syncPlantHistories cleans them and sets the top-level current
        // values (rate, hours, unit) from today's entries.
        const synced = syncPlantHistories({
          hourlyRate: pa.hourlyRate,
          hourlyRateHistory: pa.hourlyRateHistory || [],
          hoursAllocated: pa.hoursAllocated,
          hoursUnit: pa.hoursUnit,
          hoursUnitOther: pa.hoursUnitOther,
          coverageHistory: pa.coverageHistory || [],
        });
        const plantCode = String(pa.plantCode).toUpperCase().trim();
        const service = String(pa.service || '').trim() || DEFAULT_SERVICE;
        // This plant's PO (or null). The amount is cast to a number here;
        // the service's normalisePlantPo re-checks the shape and mirrors
        // po.number into `currentPO` for Invoice Draft auto-fill.
        const poRow = allPlantsPo || posByPlant.get(plantCode);
        const poAmount = poRow ? Number(poRow.amount) : null;
        const poCarriesDates =
          poRow &&
          (poRow.type === 'Yearly PO' || poRow.type === 'Quarterly PO');
        const po = poRow
          ? {
              type: poRow.type,
              number: (poRow.number || '').trim(),
              amount: Number.isFinite(poAmount) ? poAmount : null,
              initiationDate: poCarriesDates
                ? poRow.initiationDate || null
                : null,
              expirationDate: poCarriesDates
                ? poRow.expirationDate || null
                : null,
              notes: (poRow.notes || '').trim(),
            }
          : null;
        return {
          plantCode,
          service,
          // Per-plant PO — Blanket or Monthly, one per plant. currentPO
          // mirrors the PO number for legacy readers.
          po,
          currentPO: po ? po.number : '',
          hourlyRate:
            synced.hourlyRate === '' || synced.hourlyRate == null
              ? null
              : Number(synced.hourlyRate),
          hourlyRateHistory: synced.hourlyRateHistory,
          hoursAllocated:
            synced.hoursAllocated === '' || synced.hoursAllocated == null
              ? null
              : Number(synced.hoursAllocated),
          hoursUnit: synced.hoursUnit || '',
          coverageHistory: synced.coverageHistory,
          // Default to Active so legacy rows edited without touching the
          // dropdown don't accidentally save as an empty status.
          coverageStatus: pa.coverageStatus || 'Active',
          // ISO start date (YYYY-MM-DD) or '' if not recorded. The
          // <input type="date"> guarantees the format, so we only
          // trim defensively in case a legacy value sneaks through.
          startDate: (pa.startDate || '').trim(),
          endDate: (pa.endDate || '').trim(),
          // Weekend billing flags; anything but an explicit false = billed.
          billSaturdays: pa.billSaturdays === true,
          billSundays: pa.billSundays === true,
          // Free-form per-plant notes — trimmed so trailing whitespace
          // doesn't survive into the read view.
          notes: (pa.notes || '').trim(),
        };
      });
    // POs now live per-plant inside plantAssignments (see above). The
    // legacy supplier-level flat columns are explicitly blanked on every
    // save so (a) stale values don't linger in Excel / Power BI exports
    // and (b) the service's decode legacy-carry never re-seeds a PO the
    // user deleted. SharePoint currency/date columns reject empty
    // strings, so those are cleared with null.
    // `pos` is form-only state — stripped so it never becomes a
    // SharePoint field.
    // eslint-disable-next-line no-unused-vars
    const { pos: _pos, ...modelRest } = model;
    onSave({
      ...modelRest,
      SupplierCode: model.SupplierCode.toUpperCase().trim(),
      ReportingCadence: (model.ReportingCadence || '').trim(),
      BillingCycle: (model.BillingCycle || '').trim(),
      PaymentTerms: (model.PaymentTerms || '').trim(),
      PoType: '',
      BlanketPoNumber: '',
      BlanketPoAmount: null,
      BlanketPoInitiationDate: null,
      BlanketPoExpirationDate: null,
      MonthlyPoNumber: '',
      MonthlyPoAmount: null,
      // SharePoint date columns reject empty strings — send null when
      // the paperwork checkbox is off so the column is cleared cleanly.
      NewSupplierPaperworkDate: model.NewSupplierPaperworkDate || null,
      contacts,
      plantAssignments,
    },
    // Second arg: attached PO files, one entry per PO row that has a
    // file. Passed separately from the model so File objects never get
    // written as SharePoint fields — the parent uploads each to the
    // supplier's Documents after save (Category = the PO type).
    activePos
      .filter((po) => po.file)
      .map((po) => {
        const code = String(po.plantCode || '').toUpperCase().trim();
        return {
          file: po.file,
          category: po.type,
          number: (po.number || '').trim(),
          // Readable label for the document title — the sentinel would
          // otherwise leak into "Blanket PO 123 (__ALL__)".
          plantCode: code === ALL_PLANTS ? 'All Plants' : code,
        };
      }));
  };

  const inputs = !readOnly;

  return (
    <form onSubmit={submit} noValidate>
      <Section title="Supplier Info">
        <div className="form-grid">
          <TextField
            label="Name *"
            value={model.Title}
            onChange={(v) => set('Title', v)}
            error={errors.Title}
            required
            disabled={!inputs}
            full
          />
          {/*
            DUNS Number — optional 9-digit Dun & Bradstreet identifier.
            We use a raw input (same pattern as Account Number below) so
            we can strip non-digits and cap the length at 9 the moment
            the user types, instead of rejecting it at submit.
          */}
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>DUNS Number</label>
            <input
              type="text"
              inputMode="numeric"
              value={model.DunsNumber || ''}
              onChange={(e) => {
                const next = e.target.value.replace(/\D/g, '').slice(0, 9);
                set('DunsNumber', next);
                // Clear any prior validation error the moment the value
                // becomes valid (empty or exactly 9 digits).
                if (!next || /^\d{9}$/.test(next)) {
                  setErrors((prev) => {
                    if (!prev.DunsNumber) return prev;
                    const { DunsNumber: _removed, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              disabled={!inputs}
              placeholder="9-digit number"
            />
            {errors.DunsNumber && (
              <span className="error">{errors.DunsNumber}</span>
            )}
          </div>
          <div className="field">
            <label>Account Number *</label>
            <input
              type="text"
              value={model.SupplierCode || ''}
              onChange={(e) => {
                // Letters and digits only, uppercased, capped at 4
                // characters (per Jillian, Sep 2026). Spaces, hyphens
                // and punctuation are stripped as you type. Uppercasing
                // keeps stored codes consistent with how they're
                // compared everywhere else (takenCodes, plant codes).
                const next = e.target.value
                  .replace(/[^A-Za-z0-9]/g, '')
                  .toUpperCase()
                  .slice(0, 4);
                set('SupplierCode', next);
                // Live duplicate check — surface the error the moment
                // the user finishes typing a code that's already in
                // use, instead of making them wait until Save. Runs
                // for new suppliers AND for Directors editing an
                // existing supplier's code (the only path that can
                // type into this field on an edit). Self-collision is
                // ignored so saving without changing the value never
                // flags an error.
                const upper = next.toUpperCase();
                const originalUpper = originalCode.toUpperCase();
                const isCollision =
                  next &&
                  takenCodes.has(upper) &&
                  (isNew || upper !== originalUpper);
                if (isCollision) {
                  setErrors((prev) => ({
                    ...prev,
                    SupplierCode: isNew
                      ? `Account number ${next} is already in use. Try ${proposedCode}.`
                      : `Account number ${next} is already used by another supplier.`,
                  }));
                } else {
                  setErrors((prev) => {
                    if (!prev.SupplierCode) return prev;
                    const { SupplierCode: _removed, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              required
              maxLength={4}
              placeholder="Up to 4 letters or numbers"
              // Editable for new suppliers (any role) and for existing
              // suppliers when the signed-in user is a Director. Locked
              // for everyone else so RMs/liaisons can't accidentally
              // orphan timesheets by retyping a supplier's code.
              disabled={!inputs || (!isNew && !isDirector)}
            />
            {errors.SupplierCode && (
              <span className="error">{errors.SupplierCode}</span>
            )}
            {isNew && !errors.SupplierCode && (
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--brand-muted)',
                  marginTop: 4,
                }}
              >
                Auto-suggested next available number: {proposedCode}
              </span>
            )}
            {/* Director-only warning. Surfaces only when an existing
                supplier's Account Number has actually been edited to
                a different value. Plain text rather than a real Alert
                component because this lives inside a tight form field
                column and a full alert would crowd the layout. */}
            {!isNew &&
              isDirector &&
              !errors.SupplierCode &&
              (model.SupplierCode || '').trim() !== originalCode && (
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--brand-danger)',
                    marginTop: 4,
                    lineHeight: 1.4,
                  }}
                >
                  Changing account number won't update previously
                  submitted Billing Trackers or Invoices, they'll
                  still reference the original number.
                </span>
              )}
          </div>
          <TextField
            label="Tax ID #"
            value={model.TaxId}
            onChange={(v) => set('TaxId', v)}
            disabled={!inputs}
          />
          <SelectField
            label="Reporting Schedule *"
            value={cadenceMode}
            onChange={onCadenceModeChange}
            options={[...REPORTING_CADENCE_PRESETS, 'Other']}
            error={cadenceMode === 'Other' ? undefined : errors.ReportingCadence}
            required
            disabled={!inputs}
            full
          />
          {cadenceMode === 'Other' && (
            <TextField
              label="Reporting Schedule (specify) *"
              value={model.ReportingCadence}
              onChange={(v) => set('ReportingCadence', v)}
              error={errors.ReportingCadence}
              required
              disabled={!inputs}
              full
            />
          )}
          {/*
            New-supplier paperwork checkbox. Ticking it stamps today's
            date onto the record (YYYY-MM-DD); un-ticking clears it.
            Re-ticking an already-stamped supplier preserves the
            original date so we don't overwrite historical completion
            info.
          */}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              gridColumn: '1 / -1',
              cursor: inputs ? 'pointer' : 'default',
              fontSize: 14,
            }}
          >
            <input
              type="checkbox"
              checked={!!model.NewSupplierPaperworkDate}
              disabled={!inputs}
              onChange={(e) => {
                if (e.target.checked) {
                  if (!model.NewSupplierPaperworkDate) {
                    const today = new Date();
                    const iso = `${today.getFullYear()}-${String(
                      today.getMonth() + 1,
                    ).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                    set('NewSupplierPaperworkDate', iso);
                  }
                } else {
                  set('NewSupplierPaperworkDate', '');
                }
              }}
            />
            <span>Approved Vendor Form Completed</span>
          </label>
          {/* Overage-approval policy — when ticked, the Billing Tracker
              requires a written-approval file upload before a liaison can
              submit any week where this supplier goes over standard hours.
              Billing info — Directors only (canSeeBilling). */}
          {canSeeBilling && (
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              gridColumn: '1 / -1',
              cursor: inputs ? 'pointer' : 'default',
              fontSize: 14,
            }}
          >
            <input
              type="checkbox"
              checked={model.RequireOverageApproval === 'Yes'}
              disabled={!inputs}
              onChange={(e) =>
                set('RequireOverageApproval', e.target.checked ? 'Yes' : '')
              }
            />
            <span>Requires written approval for overage (hours over standard)</span>
          </label>
          )}
        </div>
      </Section>

      <Section title="Supplier Address">
        <div className="form-grid">
          <TextField
            label="Street *"
            value={model.AddressStreet}
            onChange={(v) => set('AddressStreet', v)}
            error={errors.AddressStreet}
            required
            disabled={!inputs}
            full
          />
          <TextField
            label="Address Line 2"
            value={model.AddressStreet2}
            onChange={(v) => set('AddressStreet2', v)}
            disabled={!inputs}
            full
          />
          <TextField
            label="Address Line 3"
            value={model.AddressStreet3}
            onChange={(v) => set('AddressStreet3', v)}
            disabled={!inputs}
            full
          />
          <SelectField
            label="Country *"
            value={model.Country}
            onChange={(v) => set('Country', v)}
            options={countryOptions}
            placeholder="— select country —"
            error={errors.Country}
            required
            disabled={!inputs}
          />
          {countryHasStateList(model.Country) ? (
            <SelectField
              label={`State / Province${countryRequiresState(model.Country) ? ' *' : ''}`}
              value={model.State}
              onChange={(v) => set('State', v)}
              options={stateOptions}
              placeholder={
                model.Country ? '— select state/province —' : '— select country first —'
              }
              error={errors.State}
              required={countryRequiresState(model.Country)}
              disabled={!inputs}
            />
          ) : (
            <TextField
              label={`State / Province${countryRequiresState(model.Country) ? ' *' : ''}`}
              value={model.State}
              onChange={(v) => set('State', v)}
              error={errors.State}
              required={countryRequiresState(model.Country)}
              disabled={!inputs || !model.Country}
              placeholder={
                model.Country
                  ? 'Enter state / province / region'
                  : '— select country first —'
              }
            />
          )}
          <TextField
            label="City *"
            value={model.City}
            onChange={(v) => set('City', v)}
            error={errors.City}
            required
            disabled={!inputs}
          />
          <TextField
            label="Zip / Postal Code *"
            value={model.PostalCode}
            onChange={(v) => set('PostalCode', v)}
            error={errors.PostalCode}
            required
            disabled={!inputs}
          />
        </div>
      </Section>

      {/* Billing & Invoicing — Directors only (per Jillian, July 2026).
          A non-Director editor saves the supplier with the stored
          billing values passing through untouched; validation for
          these fields is skipped for them too. */}
      {canSeeBilling && (
      <Section title="Billing &amp; Invoicing">
        <div className="form-grid">
          <SelectField
            label="Bill Payment Schedule *"
            value={billingMode}
            onChange={onBillingModeChange}
            options={[...BILLING_CYCLE_PRESETS, 'Other']}
            error={billingMode === 'Other' ? undefined : errors.BillingCycle}
            required
            disabled={!inputs}
            full
          />
          {billingMode === 'Other' && (
            <TextField
              label="Bill Payment Schedule (specify) *"
              value={model.BillingCycle}
              onChange={(v) => set('BillingCycle', v)}
              error={errors.BillingCycle}
              required
              disabled={!inputs}
              full
            />
          )}
          <SelectField
            label="PO Required *"
            value={model.PoRequired}
            onChange={(v) => set('PoRequired', v)}
            options={['Yes', 'No']}
            error={errors.PoRequired}
            required
            disabled={!inputs}
            full
          />
          {/* Per-plant POs appear once PO Required = Yes. Each plant this
              supplier covers can hold one PO — Blanket or Monthly — and
              the section repeats per plant. Blanket POs feed the
              running-balance tracker on the Supplier detail page and the
              PO Tracker tab. */}
          {model.PoRequired === 'Yes' && (
            <div className="field full">
              <label>Plant POs</label>
              {errors.pos && (
                <p className="error" style={{ margin: '4px 0 8px', fontSize: 13 }}>
                  {errors.pos}
                </p>
              )}
              {assignedPlantCodes.length === 0 && (
                <p
                  style={{
                    margin: '4px 0 8px',
                    fontSize: 13,
                    color: 'var(--brand-muted)',
                  }}
                >
                  Add a plant under Plant Locations &amp; Coverage first —
                  each PO belongs to one of this supplier&apos;s plants.
                </p>
              )}
              <div style={ADD_BUTTON_ROW_STYLE}>
                {inputs && (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={addPo}
                    // One PO per plant — once every assigned plant has a
                    // PO row (or there are no plants yet) there's
                    // nothing to add to. An "All Plants" row covers
                    // everything on its own, so it also disables Add.
                    disabled={
                      (model.pos || []).length >= assignedPlantCodes.length ||
                      (model.pos || []).some(
                        (p) =>
                          String(p.plantCode || '').toUpperCase().trim() ===
                          ALL_PLANTS,
                      )
                    }
                  >
                    + Add PO
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {(model.pos || []).map((po, i) => {
                  const poErr = (errors.posRows && errors.posRows[i]) || {};
                  // Plants already claimed by the OTHER PO rows — hidden
                  // from this row's dropdown so one plant can't hold two
                  // POs. The row's own saved value always stays visible
                  // (covers a plant assignment that was since removed).
                  const usedByOthers = new Set(
                    (model.pos || [])
                      .filter((_, idx) => idx !== i)
                      .map((p) => String(p.plantCode || '').toUpperCase().trim())
                      .filter(Boolean),
                  );
                  const rowCode = String(po.plantCode || '')
                    .toUpperCase()
                    .trim();
                  const plantOptions = Array.from(
                    new Set(
                      [...assignedPlantCodes, rowCode].filter(Boolean),
                    ),
                  )
                    .filter(
                      (code) =>
                        !usedByOthers.has(code) && code !== ALL_PLANTS,
                    )
                    .sort()
                    .map((code) => ({
                      value: code,
                      label: plantLabelByCode.get(code) || code,
                    }));
                  // "All Plants" — one PO that covers every plant.
                  // Always offered (per Jillian, July 2026); validation
                  // asks for the other PO rows to be removed if it's
                  // picked alongside per-plant ones.
                  plantOptions.unshift({
                    value: ALL_PLANTS,
                    label: 'All Plants',
                  });
                  return (
                    <div key={i} style={CARD_STYLE}>
                      <div style={CARD_HEADER_STYLE}>
                        <span style={CARD_LABEL_STYLE}>PO {i + 1}</span>
                        {inputs && (
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            onClick={() => removePo(i)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="form-grid">
                        <SelectField
                          label="Plant *"
                          value={po.plantCode || ''}
                          onChange={(v) => setPo(i, { plantCode: v })}
                          options={plantOptions}
                          error={poErr.plantCode}
                          required
                          disabled={!inputs}
                        />
                        <SelectField
                          label="PO Type *"
                          value={po.type || ''}
                          onChange={(v) =>
                            setPo(i, {
                              type: v,
                              // Start/Expiration dates only apply to the
                              // balance-carrying types (Yearly / Quarterly);
                              // clear them on a switch so stale values don't
                              // survive a type change. PO Pending has no
                              // number, so clear it too.
                              ...(v === 'Yearly PO' || v === 'Quarterly PO'
                                ? {}
                                : { initiationDate: '', expirationDate: '' }),
                              ...(v === 'PO Pending' ? { number: '' } : {}),
                            })
                          }
                          options={[
                            'Yearly PO',
                            'Quarterly PO',
                            'Monthly PO',
                            'PO Pending',
                          ]}
                          error={poErr.type}
                          required
                          disabled={!inputs}
                        />
                        {po.type !== 'PO Pending' && (
                          <TextField
                            label="PO Number"
                            value={po.number}
                            onChange={(v) => setPo(i, { number: v })}
                            error={poErr.number}
                            disabled={!inputs}
                          />
                        )}
                        <TextField
                          label={
                            po.type === 'Monthly PO'
                              ? 'Monthly Amount'
                              : po.type === 'PO Pending'
                                ? 'Expected Amount'
                                : 'PO Amount'
                          }
                          type="number"
                          value={po.amount}
                          onChange={(v) => setPo(i, { amount: v })}
                          error={poErr.amount}
                          disabled={!inputs}
                        />
                        {(po.type === 'Yearly PO' ||
                          po.type === 'Quarterly PO') && (
                          <>
                            <TextField
                              label="PO Start Date"
                              type="date"
                              value={po.initiationDate}
                              onChange={(v) => setPo(i, { initiationDate: v })}
                              error={poErr.initiationDate}
                              disabled={!inputs}
                            />
                            <TextField
                              label="PO Expiration Date"
                              type="date"
                              value={po.expirationDate}
                              onChange={(v) => setPo(i, { expirationDate: v })}
                              error={poErr.expirationDate}
                              disabled={!inputs}
                            />
                          </>
                        )}
                        {/* Free-text notes for this PO — all types (per
                            Jillian, Aug 2026). Sits above the PO File. */}
                        <div className="field full">
                          <TextArea
                            label="Notes"
                            value={po.notes || ''}
                            onChange={(v) => setPo(i, { notes: v })}
                            rows={3}
                            disabled={!inputs}
                          />
                        </div>
                        {/* The actual PO file. Uploaded to this supplier's
                            Documents on Save (Category = the PO type) so it
                            can be downloaded from the supplier page. */}
                        <div className="field full">
                          <label>PO File</label>
                          <input
                            type="file"
                            onChange={(e) =>
                              setPo(i, { file: e.target.files?.[0] || null })
                            }
                            disabled={!inputs}
                          />
                          <span
                            style={{
                              fontSize: 12,
                              color: 'var(--brand-muted)',
                              marginTop: 4,
                            }}
                          >
                            {po.file
                              ? `${po.file.name}${
                                  Number.isFinite(po.file.size)
                                    ? ` · ${(po.file.size / (1024 * 1024)).toFixed(1)} MB`
                                    : ''
                                }`
                              : 'Attach the signed PO (PDF, Word, etc.). It saves to this supplier’s Documents on Save, where it can be downloaded.'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Payment terms — optional. Net 30 / 60 / 90 cover the
              typical AR cycle; "Other" reveals a free-text fill-in
              for supplier-specific arrangements ("2/10 Net 30", "Due
              on Receipt", etc.). The chosen value flows onto the
              Supplier detail page and into the Invoices flow when
              we eventually surface payment-terms there. */}
          <SelectField
            label="Payment Terms"
            value={paymentTermsMode}
            onChange={onPaymentTermsModeChange}
            options={[...PAYMENT_TERMS_PRESETS, 'Other']}
            error={
              paymentTermsMode === 'Other' ? undefined : errors.PaymentTerms
            }
            disabled={!inputs}
            full
          />
          {paymentTermsMode === 'Other' && (
            <TextField
              label="Payment Terms (specify) *"
              value={model.PaymentTerms}
              onChange={(v) => set('PaymentTerms', v)}
              error={errors.PaymentTerms}
              required
              disabled={!inputs}
              full
            />
          )}
        </div>
      </Section>
      )}

      <Section title="Plant Locations &amp; Coverage">
        <p className="muted tiny" style={{ marginTop: 0, marginBottom: 12 }}>
          Attach this supplier to one or more plants. Each plant gets its
          own hourly rate and allocated hours.
        </p>
        {errors.plantAssignments && (
          <p className="error" style={{ margin: '0 0 8px', fontSize: 13 }}>
            {errors.plantAssignments}
          </p>
        )}
        <div style={ADD_BUTTON_ROW_STYLE}>
          {inputs && (
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={addPlantAssignment}
            >
              + Add Plant
            </button>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(model.plantAssignments || []).length === 0 ? (
            <p className="muted tiny" style={{ margin: 0 }}>
              No plant locations yet.
            </p>
          ) : (
            (model.plantAssignments || []).map((pa, i) => {
              const paErr =
                (errors.plantAssignmentsRows && errors.plantAssignmentsRows[i]) ||
                {};
              // Build the dropdown options: every known plant code +
              // whatever is already saved on the row (covers the edge
              // case where a plant was deleted but the assignment
              // lingers). Rendered as { value, label } pairs so users
              // see "SHAP — Sterling Heights" instead of the raw code.
              const plantLabelMap = new Map(
                (plants || [])
                  .filter((p) => p.PlantCode)
                  .map((p) => [
                    String(p.PlantCode).toUpperCase(),
                    p.Title ? `${p.PlantCode} — ${p.Title}` : p.PlantCode,
                  ]),
              );
              const plantCodes = Array.from(
                new Set(
                  [
                    ...(plants || []).map((p) => p.PlantCode).filter(Boolean),
                    pa.plantCode,
                  ]
                    .filter(Boolean)
                    .map((c) => String(c).toUpperCase()),
                ),
              ).sort();
              const plantOptions = plantCodes.map((code) => ({
                value: code,
                label: plantLabelMap.get(code) || code,
              }));
              return (
                <div key={i} style={CARD_STYLE}>
                  <div style={CARD_HEADER_STYLE}>
                    <span style={CARD_LABEL_STYLE}>Plant {i + 1}</span>
                    {inputs && (
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => removePlantAssignment(i)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="form-grid">
                    <SelectField
                      label="Plant *"
                      value={pa.plantCode || ''}
                      onChange={(v) => setPlantAssignment(i, { plantCode: v })}
                      options={plantOptions}
                      error={paErr.plantCode}
                      required
                      disabled={!inputs}
                      full
                    />
                    {/* Service — what this row bills for. Lets the same
                        plant appear more than once at different rates
                        (e.g. "Standard Coverage" and "Lineside Support").
                        Pre-filled with the standard name so most rows
                        never need it touched. */}
                    <SelectField
                      label="Service"
                      value={pa.service || DEFAULT_SERVICE}
                      onChange={(v) => setPlantAssignment(i, { service: v })}
                      // A saved value that isn't in the preset list (e.g. a
                      // row typed in before this became a dropdown) stays
                      // selectable so re-saving never silently changes it.
                      options={
                        pa.service && !SERVICE_OPTIONS.includes(pa.service)
                          ? [...SERVICE_OPTIONS, pa.service]
                          : SERVICE_OPTIONS
                      }
                      error={paErr.service}
                      disabled={!inputs}
                      full
                    />
                    {/* The old free-text "Current PO" field moved: POs are
                        now managed per plant in the Plant POs section
                        above, and the assignment's currentPO value is
                        derived from the PO number on save. */}
                    {/*
                      Coverage Status — per-plant lifecycle flag. Not
                      required (defaults to Active on new rows) and not
                      validated, so toggling a plant to Paused/Closed is
                      a single click with no ripple effects on Save.
                    */}
                    <SelectField
                      label="Coverage Status"
                      value={pa.coverageStatus || 'Active'}
                      onChange={(v) =>
                        setPlantAssignment(i, { coverageStatus: v })
                      }
                      options={COVERAGE_STATUSES}
                      disabled={!inputs}
                    />
                    {/* Start Date — when this supplier began covering
                        this plant. Optional and not validated; blank is
                        rendered as "—" on the Supplier detail page. */}
                    <TextField
                      label="Start Date"
                      type="date"
                      value={pa.startDate || ''}
                      onChange={(v) => setPlantAssignment(i, { startDate: v })}
                      disabled={!inputs}
                    />
                    {/* End Date — last day of coverage at this plant.
                        Optional. Must be on/after Start Date. Once it has
                        passed, a live status flips to "Closed" (here as
                        you type it, and again on read/save in
                        services/suppliers.js) and an "Ended" badge shows. */}
                    <TextField
                      label="End Date"
                      type="date"
                      value={pa.endDate || ''}
                      onChange={(v) => {
                        const next = applyEndedStatus({ ...pa, endDate: v });
                        setPlantAssignment(
                          i,
                          next === pa || next.coverageStatus === pa.coverageStatus
                            ? { endDate: v }
                            : { endDate: v, coverageStatus: next.coverageStatus },
                        );
                      }}
                      error={paErr.endDate}
                      disabled={!inputs}
                    />
                  </div>

                  {/* Coverage History + Rate History (Oct 2026): dated
                      changes, each with an Effective Date, so the Billing
                      Tracker, Approvals and invoices use the amount in
                      effect on each day. Rate History is Director-only. */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: canSeeBilling
                        ? 'repeat(auto-fit, minmax(280px, 1fr))'
                        : '1fr',
                      gap: 12,
                      marginTop: 12,
                    }}
                  >
                    <PlantHistoryBox
                      kind="coverage"
                      history={pa.coverageHistory}
                      readOnly={!inputs}
                      error={paErr.coverageHistory}
                      defaultDate={pa.startDate}
                      onChange={(next) => {
                        const synced = syncPlantHistories({
                          ...pa,
                          coverageHistory: next,
                          hoursAllocated: next.length ? pa.hoursAllocated : '',
                          hoursUnit: next.length ? pa.hoursUnit : '',
                          hoursUnitOther: '',
                        });
                        setPlantAssignment(i, {
                          coverageHistory: synced.coverageHistory,
                          hoursAllocated: synced.hoursAllocated ?? '',
                          hoursUnit: synced.hoursUnit || '',
                          hoursUnitOther: '',
                        });
                      }}
                    />
                    {canSeeBilling && (
                      <PlantHistoryBox
                        kind="rate"
                        history={pa.hourlyRateHistory}
                        readOnly={!inputs}
                        error={paErr.hourlyRateHistory}
                        defaultDate={pa.startDate}
                        onChange={(next) => {
                          const synced = syncPlantHistories({
                            ...pa,
                            hourlyRateHistory: next,
                            hourlyRate: next.length ? pa.hourlyRate : '',
                          });
                          setPlantAssignment(i, {
                            hourlyRateHistory: synced.hourlyRateHistory,
                            hourlyRate: synced.hourlyRate ?? '',
                          });
                        }}
                      />
                    )}
                  </div>

                  {/* Weekend Hours Billed (Nov 2026) — some suppliers pay
                      for weekend work at plants that run weekends, others
                      don't. Unticked days fill in as 0 hours on the
                      Billing Tracker. Hidden for weekly / monthly
                      coverage, which bills in one lump. */}
                  {!isLumpSumUnit(pa.hoursUnit) && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                        Weekend Hours Billed
                      </div>
                      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                        {[
                          ['billSaturdays', 'Saturday'],
                          ['billSundays', 'Sunday'],
                        ].map(([key, label]) => (
                          <label
                            key={key}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              fontSize: 14,
                              cursor: inputs ? 'pointer' : 'default',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={pa[key] === true}
                              disabled={!inputs}
                              onChange={(e) =>
                                setPlantAssignment(i, { [key]: e.target.checked })
                              }
                            />
                            <span>{label}</span>
                          </label>
                        ))}
                      </div>
                      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                        Unchecked days fill in as 0 hours on the Billing Tracker.
                        Only matters when the plant runs that day.
                      </div>
                    </div>
                  )}

                  {/* Per-plant notes — free-form, optional. Surfaced
                      on the Supplier Detail page beside the plant row
                      so context like "billed through parent company"
                      or "swing-shift only" travels with the
                      assignment. */}
                  <div style={{ marginTop: 12 }}>
                    <TextArea
                      label="Notes"
                      value={pa.notes || ''}
                      onChange={(v) => setPlantAssignment(i, { notes: v })}
                      rows={3}
                      disabled={!inputs}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Section>

      <Section title="Supplier Contacts" divider={false}>
        {errors.contacts && (
          <p className="error" style={{ margin: '0 0 8px', fontSize: 13 }}>
            {errors.contacts}
          </p>
        )}
        <div style={ADD_BUTTON_ROW_STYLE}>
          {inputs && (
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={addContact}
            >
              + Add Contact
            </button>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(model.contacts || []).map((c, i) => {
            const cErr =
              (errors.contactsRows && errors.contactsRows[i]) || {};
            return (
              <div key={i} style={CARD_STYLE}>
                <div style={CARD_HEADER_STYLE}>
                  <span style={CARD_LABEL_STYLE}>Contact {i + 1}</span>
                  {inputs && (
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      {/* Up / Down arrows let the user reorder contacts.
                          Disabled at the list boundaries — visible but
                          inert so the row layout stays stable. */}
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => moveContact(i, -1)}
                        disabled={i === 0}
                        title="Move up"
                        aria-label="Move contact up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => moveContact(i, 1)}
                        disabled={i === (model.contacts || []).length - 1}
                        title="Move down"
                        aria-label="Move contact down"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => removeContact(i)}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                {/* Two stacked form-grid rows: identity fields on top
                    (Name / Title / Email), phones below (Cell Phone /
                    Office Phone). Splitting at the grid level keeps
                    each row's auto-fit behavior independent so the
                    phones stay together on a second line instead of
                    flowing onto the end of row 1 on wide modals. */}
                <div className="form-grid">
                  <TextField
                    label="Name *"
                    value={c.name}
                    onChange={(v) => setContact(i, { name: v })}
                    error={cErr.name}
                    required
                    disabled={!inputs}
                  />
                  <TextField
                    label="Title *"
                    value={c.title}
                    onChange={(v) => setContact(i, { title: v })}
                    error={cErr.title}
                    required
                    disabled={!inputs}
                  />
                  <TextField
                    label="Location"
                    value={c.location || ''}
                    onChange={(v) => setContact(i, { location: v })}
                    disabled={!inputs}
                  />
                  <TextField
                    label="Email *"
                    value={c.email}
                    onChange={(v) => setContact(i, { email: v })}
                    error={cErr.email}
                    required
                    disabled={!inputs}
                  />
                </div>
                <div className="form-grid" style={{ marginTop: 12 }}>
                  <PhoneField
                    label="Cell Phone"
                    value={c.phone}
                    onChange={(v) => setContact(i, { phone: v })}
                    disabled={!inputs}
                  />
                  <PhoneField
                    label="Office Phone"
                    value={c.officePhone || ''}
                    onChange={(v) => setContact(i, { officePhone: v })}
                    disabled={!inputs}
                    allowExtension
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Notes — kept at the very bottom of the form (after every section)
          so it reads as a catch-all for anything not captured above.
          Director-only (per Jillian, Sep 2026): hidden for every other
          editor. The saved value still rides through `modelRest` on save,
          so a non-Director editing the supplier can't wipe it. */}
      {isDirector && (
        <div className="form-grid" style={{ marginTop: 24 }}>
          <TextArea
            label="Notes"
            value={model.Notes}
            onChange={(v) => set('Notes', v)}
            disabled={!inputs}
          />
        </div>
      )}

      {/* Footer row: "* Required fields" on the left, Cancel + Save on
          the right. Matches PartForm, PlantForm, TeamForm, AgreementForm. */}
      <hr style={{ margin: '20px 0', border: 'none', borderTop: '1px solid var(--brand-border)' }} />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          * Required fields
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-outline" onClick={onCancel}>
            Cancel
          </button>
          {inputs && (
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Supplier'}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}

/**
 * Coverage History / Rate History box on a plant row (Oct 2026). Lists the
 * dated changes newest first with Current / Upcoming badges, and a
 * "+ Change" form to add one with an Effective Date (back-dated or
 * scheduled). An empty history opens straight to the form as the starting
 * entry. Entries are { effectiveDate, hoursAllocated, hoursUnit } for
 * coverage and { effectiveDate, hourlyRate } for rates.
 */
function PlantHistoryBox({ kind, history, readOnly, error, defaultDate, onChange }) {
  const isRate = kind === 'rate';
  const list = Array.isArray(history) ? history : [];
  const today = todayIso();
  const [drafting, setDrafting] = useState(false);
  const [dRate, setDRate] = useState('');
  const [dHours, setDHours] = useState('');
  const [dUnit, setDUnit] = useState('');
  const [dOther, setDOther] = useState('');
  const [dDate, setDDate] = useState('');
  const [draftErr, setDraftErr] = useState('');

  const sorted = [...list].sort((a, b) =>
    a.effectiveDate < b.effectiveDate ? -1 : a.effectiveDate > b.effectiveDate ? 1 : 0,
  );
  let current = null;
  for (const h of sorted) if (h.effectiveDate <= today) current = h;
  if (!current && sorted.length) current = sorted[0];

  const isEmpty = sorted.length === 0;
  const showForm = !readOnly && (drafting || isEmpty);
  const effectiveDraftDate =
    dDate || (isEmpty ? String(defaultDate || '').slice(0, 10) || today : today);

  const openDraft = () => {
    setDRate('');
    setDHours(current?.hoursAllocated != null ? String(current.hoursAllocated) : '');
    const u = current?.hoursUnit || '';
    if (u && !HOURS_UNITS.includes(u)) {
      setDUnit('Other');
      setDOther(u);
    } else {
      setDUnit(u);
      setDOther('');
    }
    setDDate(today);
    setDraftErr('');
    setDrafting(true);
  };
  const closeDraft = () => {
    setDrafting(false);
    setDDate('');
    setDraftErr('');
  };

  const commit = () => {
    const date = effectiveDraftDate;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setDraftErr('Pick an Effective Date.');
      return;
    }
    let entry;
    if (isRate) {
      const rate = Number(dRate);
      if (dRate === '' || !Number.isFinite(rate) || rate < 0) {
        setDraftErr('Enter the hourly rate.');
        return;
      }
      entry = { effectiveDate: date, hourlyRate: rate };
    } else {
      const hours = Number(dHours);
      if (dHours === '' || !Number.isFinite(hours) || hours < 0) {
        setDraftErr('Enter the coverage hours.');
        return;
      }
      const unit = dUnit === 'Other' ? dOther.trim() : dUnit;
      if (!unit) {
        setDraftErr('Pick a Coverage Unit.');
        return;
      }
      entry = { effectiveDate: date, hoursAllocated: hours, hoursUnit: unit };
    }
    // One entry per date: adding on a date that already has one replaces it.
    const next = [...list.filter((h) => h.effectiveDate !== date), entry].sort((a, b) =>
      a.effectiveDate < b.effectiveDate ? -1 : a.effectiveDate > b.effectiveDate ? 1 : 0,
    );
    onChange(next);
    closeDraft();
  };

  const remove = (entry) => onChange(list.filter((h) => h !== entry));

  const label = (h) =>
    isRate
      ? `$${Number(h.hourlyRate).toFixed(2)}/hr`
      : formatCoverage(h) || `${h.hoursAllocated ?? 0} Hours${h.hoursUnit ? ` / ${h.hoursUnit.replace(/^per\s+/i, '')}` : ''}`;
  const when = (h) => {
    if (h.effectiveDate === '1970-01-01') return 'from the start';
    const m = String(h.effectiveDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
    const short = m ? `${m[2]}/${m[3]}/${m[1].slice(2)}` : h.effectiveDate;
    return h.effectiveDate > today ? `from ${short}` : `since ${short}`;
  };
  const badge = (text, bg, fg) => (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.5,
        textTransform: 'uppercase',
        background: bg,
        color: fg,
        padding: '1px 6px',
        borderRadius: 4,
        marginLeft: 6,
      }}
    >
      {text}
    </span>
  );
  const smallLabel = { fontSize: 11, color: 'var(--brand-muted)' };
  const smallInput = {
    padding: '6px 8px',
    border: '1px solid var(--brand-border)',
    borderRadius: 4,
    fontSize: 14,
    width: '100%',
    boxSizing: 'border-box',
  };
  const onEnter = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
  };

  return (
    <div
      className="field"
      data-field-error={error ? 'true' : undefined}
      style={{
        border: error ? '1.5px solid var(--brand-danger)' : '1px solid var(--brand-border)',
        borderRadius: 6,
        padding: 12,
        background: error ? '#fef2f2' : undefined,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand-muted)' }}>
          {isRate ? 'Rate History *' : 'Coverage History *'}
        </span>
        {!readOnly && !showForm && (
          <button type="button" className="btn btn-outline btn-sm" onClick={openDraft}>
            {isRate ? '+ Change Rate' : '+ Change Coverage'}
          </button>
        )}
      </div>

      {showForm && (
        <div
          style={{
            background: 'var(--brand-surface-muted, #f8fafc)',
            border: '1px solid var(--brand-border)',
            borderRadius: 6,
            padding: 10,
            marginBottom: 10,
          }}
        >
          {isEmpty && (
            <div style={{ fontSize: 12, marginBottom: 8, color: 'var(--brand-muted)' }}>
              {isRate ? 'Starting Rate' : 'Starting Coverage'}
            </div>
          )}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isRate
                ? 'minmax(0,1fr) minmax(0,1.2fr)'
                : 'minmax(0,0.7fr) minmax(0,1fr) minmax(0,1.2fr)',
              gap: 8,
            }}
          >
            {isRate ? (
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={smallLabel}>Hourly Rate (USD/hr)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={dRate}
                  onChange={(e) => setDRate(e.target.value)}
                  onKeyDown={onEnter}
                  placeholder="35.00"
                  style={smallInput}
                />
              </label>
            ) : (
              <>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={smallLabel}>Hours</span>
                  <input
                    type="number"
                    step="0.25"
                    min="0"
                    value={dHours}
                    onChange={(e) => setDHours(e.target.value)}
                    onKeyDown={onEnter}
                    placeholder="4"
                    style={smallInput}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={smallLabel}>Coverage Unit</span>
                  <select
                    value={dUnit}
                    onChange={(e) => setDUnit(e.target.value)}
                    style={smallInput}
                  >
                    <option value="">— select —</option>
                    {[...HOURS_UNITS, 'Other'].map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            )}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={smallLabel}>Effective Date</span>
              <input
                type="date"
                value={effectiveDraftDate}
                onChange={(e) => setDDate(e.target.value)}
                style={smallInput}
              />
            </label>
          </div>
          {!isRate && dUnit === 'Other' && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
              <span style={smallLabel}>Coverage Unit (Specify)</span>
              <input
                type="text"
                value={dOther}
                onChange={(e) => setDOther(e.target.value)}
                onKeyDown={onEnter}
                style={smallInput}
              />
            </label>
          )}
          {draftErr && (
            <div className="error" style={{ fontSize: 12, marginTop: 6 }}>
              {draftErr}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 8 }}>
            {!isEmpty && (
              <button type="button" className="btn btn-outline btn-sm" onClick={closeDraft}>
                Cancel
              </button>
            )}
            <button type="button" className="btn btn-primary btn-sm" onClick={commit}>
              Add
            </button>
          </div>
        </div>
      )}

      {!isEmpty && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: 13 }}>
          {[...sorted].reverse().map((h, idx, arr) => (
            <li
              key={`${h.effectiveDate}-${idx}`}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
                padding: '5px 0',
                borderBottom:
                  idx < arr.length - 1 ? '1px solid var(--brand-border)' : 'none',
              }}
            >
              <span>
                <strong>{label(h)}</strong>{' '}
                <span className="muted">{when(h)}</span>
                {h === current && badge('Current', '#dcfce7', '#166534')}
                {h.effectiveDate > today && badge('Upcoming', '#fef3c7', '#92400e')}
              </span>
              {!readOnly && (
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => remove(h)}
                  title="Remove this entry"
                  aria-label="Remove this entry"
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {error && (
        <div className="error" style={{ fontSize: 12, marginTop: 6 }}>
          {error}
        </div>
      )}
    </div>
  );
}

// Today as YYYY-MM-DD in local time — used as the default effective
// date for new rate changes.
function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

